<template>
  <div class="search-modal" @click.self="close">
    <div
      class="search-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="search-modal-title"
    >
      <button type="button" class="search-modal__close" aria-label="Fechar busca" @click="close">
        <IconClose />
      </button>
      <h2 id="search-modal-title" class="search-modal__title">Busca</h2>
      <div class="search-modal__field">
        <span class="search-modal__field-icon" aria-hidden="true">
          <IconSearch />
        </span>
        <input
          ref="searchInput"
          v-model="query"
          type="text"
          class="search-modal__input"
          placeholder="Pesquisar"
          aria-label="Pesquisar"
        />
      </div>
      <ul v-if="results.length" class="search-modal__results">
        <li
          v-for="(result, index) in results"
          :key="result.anchor + '-' + index"
          class="search-modal__result"
        >
          <a class="search-modal__result-link" href="javascript:void(0)" @click="goTo(result)">
            <span class="search-modal__result-content">
              <span class="search-modal__result-text" v-html="highlight(result.title)"></span>
              <span
                v-if="result.snippet"
                class="search-modal__result-snippet"
                v-html="highlight(result.snippet)"
              ></span>
            </span>
            <span class="search-modal__result-icon" aria-hidden="true">
              <IconExternalLink />
            </span>
          </a>
        </li>
      </ul>
      <p v-else-if="isLoading" class="search-modal__empty">Buscando…</p>
      <p v-else-if="errorMessage" class="search-modal__empty">{{ errorMessage }}</p>
      <p v-else-if="normalizedQuery" class="search-modal__empty">Nenhum resultado encontrado.</p>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator'
import axios, { type CancelTokenSource } from 'axios'
import IconSearch from '@/assets/icons/IconSearch.vue'
import IconClose from '@/assets/icons/IconClose.vue'
import IconExternalLink from '@/assets/icons/IconExternalLink.vue'
import CentralDaMarcaBffService from '@/services/central-da-marca-bff.service'
import type { SearchHitDTO } from '@/data/dto/search.dto'
import { applyHighlightAndWatch } from '@/core/searchHighlight'

const DEBOUNCE_MS = 300
const MIN_QUERY_LENGTH = 2

@Component({
  components: { IconSearch, IconClose, IconExternalLink }
})
export default class SearchModal extends Vue {
  query = ''
  results: SearchHitDTO[] = []
  isLoading = false
  errorMessage = ''

  private bff = new CentralDaMarcaBffService()
  private debounceTimer: number | null = null
  private currentSource: CancelTokenSource | null = null

  mounted(): void {
    document.body.style.overflow = 'hidden'
    this.$nextTick(() => {
      const input = this.$refs.searchInput as HTMLInputElement | undefined
      input?.focus()
    })
    document.addEventListener('keydown', this.handleEscape)
  }

  beforeDestroy(): void {
    document.body.style.overflow = ''
    document.removeEventListener('keydown', this.handleEscape)
    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer)
    }
    this.currentSource?.cancel()
  }

  handleEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close()
    }
  }

  get normalizedQuery(): string {
    return this.query.trim().toLowerCase()
  }

  @Watch('query')
  onQueryChange(): void {
    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer)
    }
    const q = this.normalizedQuery
    if (q.length < MIN_QUERY_LENGTH) {
      this.results = []
      this.errorMessage = ''
      this.isLoading = false
      this.currentSource?.cancel()
      return
    }
    this.isLoading = true
    this.debounceTimer = window.setTimeout(() => {
      this.runSearch(q)
    }, DEBOUNCE_MS)
  }

  async runSearch(q: string): Promise<void> {
    this.currentSource?.cancel()
    const source = axios.CancelToken.source()
    this.currentSource = source
    this.errorMessage = ''
    try {
      const response = await this.bff.search(q, source.token)
      this.results = (response as any)?.data?.hits ?? response?.hits ?? []
    } catch (error: unknown) {
      if (axios.isCancel(error)) {
        return
      }
      this.results = []
      this.errorMessage = 'Não foi possível realizar a busca. Tente novamente.'
    } finally {
      if (this.currentSource === source) {
        this.isLoading = false
      }
    }
  }

  highlight(text: string): string {
    const q = this.normalizedQuery
    const escaped = this.escapeHtml(text)
    if (!q) {
      return escaped
    }
    const regex = new RegExp(`(${this.escapeRegex(q)})`, 'gi')
    return escaped.replace(regex, '<mark>$1</mark>')
  }

  escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  escapeRegex(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  goTo(result: SearchHitDTO): void {
    const route = result.route.startsWith('/') ? result.route : `/${result.route}`
    const hash = result.anchor ? `#${result.anchor}` : ''
    const queryParam = this.query.trim() ? { q: this.query.trim() } : undefined
    const samePath = this.$route.path === route
    const sameHash = this.$route.hash === hash

    window.scrollTo({ top: 0, behavior: 'auto' })

    if (samePath && sameHash && hash) {
      this.scrollToAnchor(hash)
      if (queryParam?.q) applyHighlightAndWatch(queryParam.q)
    } else if (samePath && !sameHash) {
      this.$router.replace({ path: route, hash, query: queryParam }).catch(() => undefined)
    } else {
      this.$router.push({ path: route, hash, query: queryParam }).catch(() => undefined)
    }
    this.close()
  }

  scrollToAnchor(hash: string): void {
    if (!hash) return
    try {
      const el = document.querySelector(hash) as HTMLElement | null
      if (el) {
        const NAVBAR_HEIGHT = 80
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const top = el.getBoundingClientRect().top + window.scrollY - NAVBAR_HEIGHT
            window.scrollTo({ top, behavior: 'smooth' })
          })
        })
      }
    } catch {
      /* */
    }
  }

  close(): void {
    this.$emit('close')
  }
}
</script>

<style lang="scss">
.search-modal {
  position: fixed;
  inset: 0;
  background: rgba(16, 16, 16, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;

  &__dialog {
    position: relative;
    background: #fff;
    border-radius: 16px;
    width: 100%;
    max-width: 720px;
    padding: 48px 56px 40px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    font-family: 'GlobotipoCorporativaRegular';
  }

  &__close {
    position: absolute;
    top: 20px;
    right: 24px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    margin: 0 0 24px;
    text-align: center;
    font-size: 40px;
    font-weight: 400;
    background: linear-gradient(
      90deg,
      #0079fd -37.91%,
      #2d51fb 20.11%,
      #5a29fa 78.13%,
      #8800f8 137.91%
    );
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: #2d51fb;
  }

  &__field {
    display: flex;
    align-items: center;
    gap: 12px;
    border: 2px solid #2d51fb;
    border-radius: 999px;
    padding: 12px 20px;
    margin-bottom: 16px;
  }

  &__field-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-family: 'GlobotipoCorporativaRegular';
    font-size: 16px;
    color: #101010;

    &::placeholder {
      font-family: 'GlobotipoCorporativaRegular';
      color: #101010;
      opacity: 0.6;
    }
  }

  &__results {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 320px;
    overflow-y: auto;
  }

  &__result {
    border-bottom: 1px solid #e5e5e5;

    &:last-child {
      border-bottom: none;
    }
  }

  &__result-link {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 8px;
    color: #101010;
    text-decoration: none;
    cursor: pointer;
    font-size: 14px;

    &:hover {
      background: rgba(45, 81, 251, 0.04);
    }

    strong {
      font-weight: 700;
    }
  }

  &__result-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
  }

  &__result-text {
    font-size: 14px;
    color: #101010;

    mark {
      background: rgba(45, 81, 251, 0.18);
      color: #2d51fb;
      font-weight: 700;
      padding: 0 2px;
      border-radius: 2px;
    }
  }

  &__result-snippet {
    font-size: 12px;
    color: #555;
    line-height: 1.4;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;

    mark {
      background: rgba(45, 81, 251, 0.18);
      color: inherit;
      padding: 0 2px;
      border-radius: 2px;
    }
  }

  &__result-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__empty {
    margin: 24px 0 0;
    text-align: center;
    color: #101010;
    opacity: 0.7;
    font-size: 14px;
  }
}
</style>
